import axios from 'axios';
import Cookies from 'js-cookie';

// Direct API URLs - no environment selection
const BASE_URL = 'https://api.tazatropics.com/public/api/v1';
const ADDRESS_BASE_URL = 'https://api.tazatropics.com/public/api/';
const MEDIA_URL = 'https://api.tazatropics.com/public/storage/images';

// Configure axios defaults
axios.defaults.headers.common['Accept'] = 'application/json';

// Helper to convert object to URLSearchParams - matching Pure Meat's JSON_to_URLEncoded
const toFormData = (element, key = null, list = []) => {
  if (typeof element === 'object' && element !== null && !(element instanceof Date)) {
    for (let idx in element) {
      toFormData(
        element[idx],
        key ? `${key}[${idx}]` : idx,
        list
      );
    }
  } else {
    list.push(`${key}=${encodeURIComponent(element)}`);
  }
  return list.join('&');
};

// Send order email using the same API as sendVerificationOnMail (which works)
export const sendOrderConfirmationEmail = async (email, subject, message, token = null) => {
  try {
    const param = {
      email: email,
      subject: subject,
      header_text: 'Order Confirmation',
      thank_you_text: 'Thank you for your order!',
      mediaURL: MEDIA_URL,
      country_code: '+1', // Dummy value since this API requires it
      mobile: '1234567890' // Dummy value since this API requires it
    };

    console.log('Sending order confirmation email with params:', param);

    const formBody = toFormData(param);
    console.log('Form body:', formBody);

    const response = await axios.post(`${BASE_URL}/sendVerificationOnMail`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    });

    console.log('Order confirmation email response:', response.data);

    if (response.data.success) {
      return { success: true, data: response.data };
    }
    return { success: false, message: response.data.message };
  } catch (error) {
    console.error('Send order confirmation email error:', error);
    console.error('Error status:', error.response?.status);
    console.error('Error response data:', error.response?.data);

    return {
      success: false,
      message: error.response?.data?.message || error.response?.data?.error || 'Failed to send order confirmation email'
    };
  }
};

// Send verification email for registration
export const sendVerificationOnMail = async (email, subject = 'Verification', country_code = '', mobile = '') => {
  try {
    const param = {
      email,
      subject,
      header_text: 'Use this code for verification',
      thank_you_text: "Don't share this otp to anybody else",
      mediaURL: MEDIA_URL,          
      country_code,
      mobile
    };

    console.log('Sending verification email with params:', param);

    const formBody = toFormData(param);
    console.log('Encoded form body:', formBody);

    const response = await axios.post(`${BASE_URL}/sendVerificationOnMail`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    });

    const data = response.data;
    console.log('sendVerificationOnMail response:', data);

    if (response.status === 200 && data.status === 200 && data.data === true && data.otp_id) {
      return { success: true, otp_id: data.otp_id };
    } else {
      return {
        success: false,
        message: data.message || 'Failed to send verification email'
      };
    }
  } catch (error) {
    console.error('sendVerificationOnMail error:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error response status:', error.response?.status);
    return {
      success: false,
      message: error.response?.data?.message || error.response?.data?.error || 'Network error. Please try again.',
      errors: error.response?.data?.errors || error.response?.data
    };
  }
};

// Register new user
export const registerUser = async (userData) => {
  try {
    console.log('Registration data being sent:', userData);
    
    const formBody = toFormData(userData);
    
    const response = await axios.post(`${BASE_URL}/auth/create_account`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    });

    const data = response.data;
    console.log('Registration response:', data);
    
    if (response.status === 200 && data.status === 200) {
      return { success: true, data: data.user, token: data.token };
    } else {
      return { 
        success: false, 
        message: data.message || 'Registration failed',
        errors: data.errors 
      };
    }
  } catch (error) {
    console.error('Register API error:', error);
    return { success: false, message: error.response?.data?.message || 'Network error. Please try again.' };
  }
};

// Login user with email or mobile
export const loginUser = async (credentials) => {
  try {
    const formBody = toFormData(credentials);
    
    const response = await axios.post(`${BASE_URL}/auth/login`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    });

    const data = response.data;
    
    if (response.status === 200 && data.status === 200) {
      // Store token in cookies for private API usage
      if (data.token) {
        Cookies.set('authToken', data.token, { expires: 7 }); // expires in 7 days
      }
      return { success: true, data: data.user, token: data.token };
    } else {
      return { 
        success: false, 
        message: data.message || 'Login failed'
      };
    }
  } catch (error) {
    console.error('Login API error:', error);
    console.error('Error response:', error.response?.data);
    
    // Extract validation errors or backend message
    const backendMsg = error.response?.data?.message || error.response?.data?.error || error.message;
    const validationErrors = error.response?.data?.errors || error.response?.data?.data?.errors;
    
    let message = backendMsg || 'Network error. Please try again.';
    
    // If we have validation errors, use them instead of generic message
    if (validationErrors && typeof validationErrors === 'object') {
      const errors = Object.values(validationErrors).flat();
      if (errors.length > 0) {
        message = errors.join(' ');
      }
    }
    
    return { success: false, message };
  }
};

// Logout user
export const logoutUser = async (userId, token) => {
  try {
    const response = await fetch(`${BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      },
      body: toFormData({ id: userId })
    });

    const data = await response.json();
    
    if (response.ok && data.status === 200) {
      return { success: true };
    } else {
      return { success: false, message: data.message || 'Logout failed' };
    }
  } catch (error) {
    console.error('Logout API error:', error);
    return { success: false, message: 'Network error. Please try again.' };
  }
};

// Verify OTP for registration
export const verifyOTP = async (otpId, otp) => {
  try {
    const formBody = toFormData({ id: otpId, otp });
    
    const response = await axios.post(`${BASE_URL}/otp/verifyOTP`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    });

    const data = response.data;
    console.log('verifyOTP response:', data);
    
    if (response.status === 200 && data.status === 200 && data.data) {
      return { success: true, data: data.data };
    } else {
      return { 
        success: false, 
        message: data.message || data.error || 'OTP verification failed'
      };
    }
  } catch (error) {
    console.error('Verify OTP API error:', error);
    return { success: false, message: error.response?.data?.message || error.response?.data?.error || 'Network error. Please try again.' };
  }
};

// Forgot password - Check if email exists first
export const forgotPassword = async (email) => {
  try {
    const formBody = toFormData({ email });
    
    // Try verifyEmailForReset endpoint
    const response = await axios.post(`${BASE_URL}/auth/verifyEmailForReset`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    });

    const data = response.data;
    console.log('forgotPassword response:', data);
    
    if (response.status === 200 && (data.status === 200 || data.success || data.data)) {
      return { 
        success: true, 
        message: data.message || 'Verification code sent to your email',
        otp_id: data.otp_id || data.data?.otp_id,
        email: email
      };
    } else {
      return { 
        success: false, 
        message: data.message || data.error || 'Email not found'
      };
    }
  } catch (error) {
    console.error('Forgot password API error:', error);
    console.error('Error response:', error.response?.data);
    return { 
      success: false, 
      message: error.response?.data?.message || error.response?.data?.error || 'Email not registered or network error' 
    };
  }
};

// Verify OTP for password reset (reset flow requires id, otp, type='email', email)
export const verifyForgotPasswordOTP = async (otpId, otp, email) => {
  try {
    if (!otpId) {
      return { success: false, message: 'Missing otp_id. Please resend OTP.' };
    }
    if (!email) {
      return { success: false, message: 'Missing email. Please enter your email again.' };
    }

    const formBody = toFormData({ id: otpId, otp, type: 'email', email });
    
    const response = await axios.post(`${BASE_URL}/otp/verifyOTPReset`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    });

    const data = response.data;
    console.log('verifyOTPReset response:', data);
    
    if (response.status === 200 && (data.status === 200 || data.success)) {
      return { 
        success: true,
        message: data.message || 'OTP verified successfully',
        token: data.token || data.data?.token || null
      };
    } else {
      return { 
        success: false, 
        message: data.message || data.error || 'Invalid OTP'
      };
    }
  } catch (error) {
    console.error('Verify OTP (reset) error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || error.response?.data?.error || 'Invalid OTP' 
    };
  }
};

// Generate temporary token for password reset (public endpoint)
export const generateTempTokenForReset = async (email) => {
  try {
    const formBody = toFormData({ email });
    const response = await axios.post(`${BASE_URL}/otp/generateTempTokenEmail`, formBody, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });
    const data = response.data;
    console.log('generateTempTokenEmail response:', data);
    const tempToken = data.token || data.temp;
    if (response.status === 200 && (data.status === 200 || data.success) && tempToken) {
      return { success: true, token: tempToken };
    }
    return { success: false, message: data.message || data.error || 'Failed to generate temp token' };
  } catch (error) {
    console.error('generateTempTokenForReset error:', error.response?.data || error.message);
    return { success: false, message: error.response?.data?.message || error.response?.data?.error || 'Failed to generate temp token' };
  }
};

// Reset password with email and new password using temp JWT
export const resetPassword = async (email, otp, newPassword, resetToken = null, otpId = null) => {
  try {
    // Use provided reset token if available, else try to generate one
    let tokenToUse = resetToken;
    if (!tokenToUse) {
      const temp = await generateTempTokenForReset(email);
      if (temp.success && temp.token) {
        tokenToUse = temp.token;
      } else {
        console.warn('Temp token generation failed, will attempt reset without token. Reason:', temp.message);
      }
    }

    // Normalize email and include password_confirmation + type
    const normalizedEmail = (email || '').trim().toLowerCase();
    const payload = { 
      email: normalizedEmail,
      password: newPassword,
      password_confirmation: newPassword,
      otp,
      type: 'email'
    };
    if (otpId) payload.id = otpId;
    const resetBody = toFormData(payload);
    
    // Try with token if available; otherwise try without Authorization header
    const headersBase = { 'Content-Type': 'application/x-www-form-urlencoded' };
    const headersWithAuth = tokenToUse ? { ...headersBase, 'Authorization': `Bearer ${tokenToUse}` } : headersBase;

    let response;
    try {
      response = await axios.post(`${BASE_URL}/password/updateUserPasswordWithEmail`, resetBody, { headers: headersWithAuth });
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || err.response?.data?.error;
      console.warn('Reset with initial headers failed:', status, msg);
      // If first attempt used token and failed unauthorized, no point retrying without.
      // If first attempt had no token and failed due to token required, bubble a clearer message.
      if (!tokenToUse && (status === 401 || /token required/i.test(msg || ''))) {
        return { success: false, message: 'Token required. Please verify OTP again to generate a reset token.' };
      }
      // Re-throw to handle below
      throw err;
    }

    const data = response.data;
    console.log('resetPassword response:', data);
    
    if (response.status === 200 && (data.status === 200 || data.success)) {
      return { 
        success: true,
        message: data.message || 'Password reset successful'
      };
    } else {
      return { 
        success: false, 
        message: data.message || data.error || 'Password reset failed'
      };
    }
  } catch (error) {
    console.error('Reset password API error:', error);
    console.error('Error response:', error.response?.data);
    // Return validation errors or backend message
    const backendMsg = error.response?.data?.message || error.response?.data?.error || error.message;
    const validationErrors = error.response?.data?.errors || error.response?.data?.data?.errors;
    let message = backendMsg || 'Password reset failed';
    if (validationErrors && typeof validationErrors === 'object') {
      const errors = Object.values(validationErrors).flat();
      if (errors.length > 0) message = errors.join(' ');
    }
    return { 
      success: false, 
      message
    };
  }
};

// Update user password (from profile page - authenticated endpoint)
export const updateUserPassword = async (userId, currentPassword, newPassword, token) => {
  try {
    // Fetch user email (endpoint requires email)
    const profileForm = toFormData({ id: userId });
    const profileResp = await axios.post(`${BASE_URL}/profile/byId`, profileForm, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });
    const email = (profileResp.data?.data?.email || '').trim().toLowerCase();
    if (!email) {
      return { success: false, message: 'Could not retrieve user email' };
    }

    // Primary attempt: use profile/update as requested
    const profilePayload = {
      id: userId,
      email,
      password: newPassword,
      password_confirmation: newPassword,
      old_password: currentPassword,
      type: 'user'
    };
    const profileUpdateForm = toFormData(profilePayload);

    const baseHeaders = { 'Content-Type': 'application/x-www-form-urlencoded', 'Authorization': `Bearer ${token}` };

    let response;
    try {
      response = await axios.post(`${BASE_URL}/profile/update`, profileUpdateForm, { headers: baseHeaders });
    } catch (profileErr) {
      const status = profileErr.response?.status;
      const msg = profileErr.response?.data?.message || profileErr.response?.data?.error;
      const isNotFound = status === 404 || /data not found/i.test(msg || '');

      // Fallback to reset endpoint with temp token (same as forgot password flow)
      const tempTokenResult = await generateTempTokenForReset(email);
      const tempToken = tempTokenResult.success ? tempTokenResult.token : null;
      const tempHeaders = tempToken
        ? { 'Content-Type': 'application/x-www-form-urlencoded', 'Authorization': `Bearer ${tempToken}` }
        : baseHeaders;

      // Payload aligned with reset flow, plus ids and current_password to satisfy validation
      const resetPayload = {
        email,
        password: newPassword,
        password_confirmation: newPassword,
        type: 'email',
        id: userId,
        user_id: userId,
        current_password: currentPassword
      };
      const resetForm = toFormData(resetPayload);

      if (isNotFound) {
        response = await axios.post(`${BASE_URL}/password/updateUserPasswordWithEmail`, resetForm, { headers: tempHeaders });
      } else {
        // If other error, try reset flow once; if it fails, bubble original error for clarity
        try {
          response = await axios.post(`${BASE_URL}/password/updateUserPasswordWithEmail`, resetForm, { headers: tempHeaders });
        } catch (resetErr) {
          throw profileErr;
        }
      }
    }

    const data = response.data;
    console.log('updateUserPassword response:', data);

    if (response.status === 200 && (data.status === 200 || data.success)) {
      return {
        success: true,
        message: data.message || 'Password updated successfully'
      };
    } else {
      return {
        success: false,
        message: data.message || data.error || 'Failed to update password'
      };
    }
  } catch (error) {
    console.error('Update password API error:', error);
    console.error('Error response:', error.response?.data);
    
    // Extract validation errors or backend message
    const backendMsg = error.response?.data?.message || error.response?.data?.error || error.message;
    const validationErrors = error.response?.data?.errors || error.response?.data?.data?.errors;
    
    let message = backendMsg || 'Failed to update password. Please try again.';
    
    if (validationErrors && typeof validationErrors === 'object') {
      const errors = Object.values(validationErrors).flat();
      if (errors.length > 0) {
        message = errors.join(' ');
      }
    }
    
    return { success: false, message };
  }
};

// Get user profile by ID
export const getUserProfile = async (userId, token) => {
  try {
    const formBody = toFormData({ id: userId });
    
    const response = await axios.post(`${BASE_URL}/profile/byId`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('getUserProfile response:', data);
    
    if (response.status === 200 && data.status === 200 && data.data) {
      return { success: true, data: data.data };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to fetch profile'
      };
    }
  } catch (error) {
    console.error('Get user profile API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.' 
    };
  }
};

// Update user profile
export const updateUserProfile = async (profileData, token) => {
  try {
    const formBody = toFormData(profileData);
    
    const response = await axios.post(`${BASE_URL}/profile/update`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('updateUserProfile response:', data);
    
    if (response.status === 200 && data.status === 200) {
      return { success: true, data: data.data };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to update profile',
        errors: data.error || data.errors
      };
    }
  } catch (error) {
    console.error('Update user profile API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.',
      errors: error.response?.data?.error || error.response?.data?.errors
    };
  }
};

// Upload profile image
export const uploadProfileImage = async (file, token) => {
  try {
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await axios.post(`${BASE_URL}/uploadImage`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('uploadProfileImage response:', data);
    
    if (response.status === 200 && data.status === 200 && data.data) {
      return { success: true, image_name: data.data.image_name };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to upload image'
      };
    }
  } catch (error) {
    console.error('Upload profile image API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.' 
    };
  }
};

// Get user orders
export const getUserOrders = async (userId, token, limit = 500000) => {
  try {
    const formBody = toFormData({ id: userId, limit });
    
    const response = await axios.post(`${BASE_URL}/orders/getByUid`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('getUserOrders response:', data);
    
    if (response.status === 200 && data.status === 200) {
      const orders = data.data || [];
      // Parse orders JSON for each order
      orders.forEach(order => {
        if (typeof order.orders === 'string') {
          order.orders = JSON.parse(order.orders);
        }
      });
      return { success: true, data: orders };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to fetch orders',
        data: []
      };
    }
  } catch (error) {
    console.error('Get user orders API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.',
      data: []
    };
  }
};

// Get user addresses
export const getUserAddresses = async (userId, token) => {
  try {
    const authToken = token || Cookies.get('authToken') || localStorage.getItem('auth_token');
    if (!authToken) {
      return { success: false, message: 'No auth token found', data: [] };
    }

    const formBody = toFormData({ id: userId });
    
    const response = await axios.post(`${ADDRESS_BASE_URL}v1/address/getByUid`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${authToken}`
      }
    });

    const data = response.data;
    console.log('getUserAddresses response:', data);
    
    if (response.status === 200 && data.status === 200) {
      return { success: true, data: data.data || [] };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to fetch addresses',
        data: []
      };
    }
  } catch (error) {
    console.error('Get user addresses API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.',
      data: []
    };
  }
};

// Add new address
export const addAddress = async (addressData, token) => {
  try {
    const formBody = toFormData(addressData);
    
    const response = await axios.post(`${ADDRESS_BASE_URL}v1/address/addNew`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('addAddress response:', data);
    
    if (response.status === 200 && data.status === 200) {
      return { success: true, data: data.data };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to add address',
        errors: data.error || data.errors
      };
    }
  } catch (error) {
    console.error('Add address API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.',
      errors: error.response?.data?.error || error.response?.data?.errors
    };
  }
};

// Update address
export const updateAddress = async (addressData, token) => {
  try {
    const formBody = toFormData(addressData);
    
    const response = await axios.post(`${ADDRESS_BASE_URL}v1/address/updateMyAddress`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('updateAddress response:', data);
    
    if (response.status === 200 && data.status === 200) {
      return { success: true, data: data.data };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to update address',
        errors: data.error || data.errors
      };
    }
  } catch (error) {
    console.error('Update address API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.',
      errors: error.response?.data?.error || error.response?.data?.errors
    };
  }
};

// Delete address
export const deleteAddress = async (addressId, token) => {
  try {
    const formBody = toFormData({ id: addressId });
    
    const response = await axios.post(`${ADDRESS_BASE_URL}v1/address/deleteMyAddress`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('deleteAddress response:', data);
    
    if (response.status === 200 && data.status === 200) {
      return { success: true };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to delete address'
      };
    }
  } catch (error) {
    console.error('Delete address API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.' 
    };
  }
};

// ============================================
// OFFERS & COUPONS APIs
// ============================================

// Get available offers/coupons
export const getOffers = async (token = null) => {
  try {
    const headers = token ? {
      'Authorization': `Bearer ${token}`
    } : {};
    
    const response = await axios.get(`${BASE_URL}/offers/getMyOffers`, { headers });

    const data = response.data;
    console.log('getOffers response:', data);
    
    if (response.status === 200 && data.status === 200 && data.data) {
      return { 
        success: true, 
        data: data.data 
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to fetch offers',
        data: []
      };
    }
  } catch (error) {
    console.error('Get offers API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.',
      data: []
    };
  }
};

// ============================================
// WALLET APIs
// ============================================

// Get user wallet balance
export const getWalletBalance = async (userId, token) => {
  try {
    const authToken = token || Cookies.get('authToken') || localStorage.getItem('auth_token');
    if (!authToken) {
      return { success: false, message: 'No auth token found', balance: 0 };
    }

    const formBody = toFormData({ id: userId });
    
    const response = await axios.post(`${BASE_URL}/profile/getMyWalletBalance`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${authToken}`
      }
    });

    const data = response.data;
    console.log('getWalletBalance response:', data);
    
    if (response.status === 200 && data.status === 200 && data.data) {
      return { 
        success: true, 
        balance: parseFloat(data.data.balance) || 0
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to fetch wallet balance',
        balance: 0
      };
    }
  } catch (error) {
    console.error('Get wallet balance API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.',
      balance: 0
    };
  }
};

// ============================================
// PAYMENT METHODS APIs
// ============================================

// Get available payment methods
export const getPaymentMethods = async (token) => {
  try {
    // If token not provided, get from cookies or localStorage
    const authToken = token || Cookies.get('authToken') || localStorage.getItem('auth_token');
    if (!authToken) {
      return { success: false, message: 'No auth token found', data: [] };
    }
    const response = await axios.get(`${BASE_URL}/payments/getPayments`, {
      headers: {
        'Authorization': `Bearer ${authToken}`
      }
    });

    const data = response.data;
    console.log('getPaymentMethods response:', data);
    
    if (response.status === 200 && data.status === 200 && data.data) {
      return { 
        success: true, 
        data: data.data 
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to fetch payment methods',
        data: []
      };
    }
  } catch (error) {
    console.error('Get payment methods API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.',
      data: []
    };
  }
};

// Send notifications to stores about new order - matching Pure Meat implementation
export const sendNotificationToStores = async (storeIds, orderId, token) => {
  try {
    const param = {
      id: Array.isArray(storeIds) ? storeIds.join(',') : storeIds,
      title: `New Order #${orderId}`,
      message: 'New Order'
    };
    
    const formBody = toFormData(param);
    
    const response = await axios.post(
      `${BASE_URL}/notification/sendToStore`,
      formBody,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      }
    );
    
    console.log('Notification sent to stores:', response.data);
    return response.data;
  } catch (error) {
    console.error('Send notification error:', error);
    // Don't throw error - notifications are not critical
    return { success: false, error: error.message };
  }
};

// Send customer order confirmation email
export const sendCustomerOrderEmail = async (orderData, token) => {
  try {
    const param = {
      id: orderData.id,
      mediaURL: orderData.mediaURL || MEDIA_URL,
      subject: orderData.subject || 'Your Order Confirmation',
      email: orderData.email,
      username: orderData.username,
      store_phone: orderData.store_phone || '1234567890',
      store_email: orderData.store_email || 'store@tazatropics.com',
      header_text: 'Order Confirmation',
      thank_you_text: 'Thank you for your order!',
    };

    console.log('Sending order confirmation email to customer with params:', param);

    const formBody = toFormData(param);

    const response = await axios.post(
      `${BASE_URL}/orders/sendMailForOrders`,
      formBody,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      }
    );

    console.log('Order confirmation email response (customer):', response.data);

    if (response.data.success || (response.data.status === 200 && response.data.data)) {
      return { success: true, data: response.data };
    }
    return {
      success: false,
      message: response.data.message || 'Failed to send order confirmation email to customer',
      details: response.data
    };
  } catch (error) {
    console.error('Send order confirmation email to customer error:', error);
    console.error('Error status:', error.response?.status);
    console.error('Error response data:', error.response?.data);

    return {
      success: false,
      message: error.response?.data?.message || error.response?.data?.error || 'Failed to send order confirmation email to customer',
      details: error.response?.data
    };
  }
};

// Get Stripe cards for user
export const getStripeCards = async (stripeKey, token) => {
  try {
    const formBody = toFormData({ id: stripeKey });
    
    const response = await axios.post(`${BASE_URL}/payments/getStripeCards`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('getStripeCards response:', data);
    
    if (response.status === 200 && data.status === 200 && data.success && data.success.data) {
      return { 
        success: true, 
        data: data.success.data 
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to fetch cards',
        data: []
      };
    }
  } catch (error) {
    console.error('Get Stripe cards API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.',
      data: []
    };
  }
};

// Create Stripe payment
export const createStripePayment = async (paymentData, token) => {
  try {
    const formBody = toFormData(paymentData);
    
    const response = await axios.post(`${BASE_URL}/payments/createStripePayments`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('createStripePayment response:', data);
    
    if (response.status === 200 && data.status === 200 && data.success && data.success.id) {
      return { 
        success: true, 
        data: data.success 
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Payment failed'
      };
    }
  } catch (error) {
    console.error('Create Stripe payment API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.error?.message || error.response?.data?.message || 'Payment failed. Please try again.'
    };
  }
};

/**
 * Get Razorpay Key from Backend
 * Backend controls whether to return test or live key based on admin configuration.
 * Same approach as Pure Meat implementation.
 */
export const getRazorpayKey = async (token) => {
  try {
    const response = await axios.get(`${BASE_URL}/getRazorPayKey`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('getRazorpayKey response:', data);
    
    if (response.status === 200 && data.status === 200 && data.data) {
      return { 
        success: true, 
        key: data.data 
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to fetch Razorpay key'
      };
    }
  } catch (error) {
    console.error('Get Razorpay key API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.'
    };
  }
};

// Verify Razorpay purchase
export const verifyRazorpayPurchase = async (paymentId, token) => {
  try {
    const response = await axios.get(`${BASE_URL}/payments/VerifyRazorPurchase?id=${paymentId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('verifyRazorpayPurchase response:', data);
    
    if (response.status === 200 && data.status === 200 && data.success && data.success.status === 'captured') {
      return { 
        success: true, 
        data: data.success 
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Payment verification failed'
      };
    }
  } catch (error) {
    console.error('Verify Razorpay purchase API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Payment verification failed. Please contact administrator.'
    };
  }
};

// ============================================
// STORES APIs
// ============================================

// Get store data by IDs
export const getStoresData = async (storeIds, token) => {
  try {
    const formBody = toFormData({ id: storeIds });
    
    const response = await axios.post(`${BASE_URL}/stores/getStoresData`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('getStoresData response:', data);
    
    if (response.status === 200 && data.status === 200 && data.data) {
      return { 
        success: true, 
        data: data.data 
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to fetch stores',
        data: []
      };
    }
  } catch (error) {
    console.error('Get stores data API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.',
      data: []
    };
  }
};

/**
 * Get PayPal Client ID from Backend
 * 
 * IMPORTANT: This returns the PayPal client ID configured in the backend admin panel.
 * The backend determines whether to return:
 * - Sandbox client ID (for testing)
 * - Live client ID (for production)
 * 
 * This ensures live/test mode switching is controlled by backend configuration,
 * not frontend environment variables. Same as Pure Meat implementation.
 * 
 * @param {string} token - Authentication token
 * @returns {Promise<{success: boolean, key?: string, message?: string}>}
 */
/**
 * Get PayPal Client ID from backend
 * 
 * AUTOMATIC ENVIRONMENT DETECTION:
 * The frontend automatically detects whether the key is for sandbox or live mode:
 * - Sandbox keys: Start with "sb-" or "AZ" pattern
 * - Live keys: Start with "A" followed by different pattern
 * 
 * ADMIN PANEL WORKFLOW:
 * 1. Admin changes keys in backend from test to live (or vice versa)
 * 2. Backend returns the new client ID via this API
 * 3. Frontend auto-detects environment from client ID prefix
 * 4. PayPal SDK loads with correct environment automatically
 * 
 * NO MANUAL CONFIGURATION NEEDED - Just change the keys in admin panel!
 */
export const getPayPalKey = async (token) => {
  try {
    const response = await axios.get(`${BASE_URL}/getPayPalKey`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('getPayPalKey response:', data);
    
    if (response.status === 200 && data.status === 200 && data.data) {
      return { 
        success: true, 
        key: data.data 
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to fetch PayPal key'
      };
    }
  } catch (error) {
    console.error('Get PayPal key API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'Network error. Please try again.'
    };
  }
};

// Create PayPal order and get approval URL
export const createPayPalOrder = async (amount, currencyCode, token) => {
  try {
    const formBody = toFormData({
      amount: amount.toFixed(2),
      currency: currencyCode
    });
    
    const response = await axios.post(`${BASE_URL}/paypal/create-order`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('createPayPalOrder response:', data);
    
    if (response.status === 200 && data.status === 200 && data.data) {
      return { 
        success: true, 
        orderId: data.data.id,
        approvalUrl: data.data.approvalUrl || data.data.approval_url
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to create PayPal order'
      };
    }
  } catch (error) {
    console.error('Create PayPal order API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'Network error. Please try again.'
    };
  }
};

// Capture PayPal payment after approval
export const capturePayPalPayment = async (orderId, token) => {
  try {
    const formBody = toFormData({ order_id: orderId });
    
    const response = await axios.post(`${BASE_URL}/paypal/capture-order`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('capturePayPalPayment response:', data);
    
    if (response.status === 200 && data.status === 200 && data.data) {
      return { 
        success: true, 
        data: data.data
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to capture PayPal payment'
      };
    }
  } catch (error) {
    console.error('Capture PayPal payment API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'Network error. Please try again.'
    };
  }
};

/**
 * Get Paystack Key from Backend
 * Backend controls whether to return test or live key based on admin configuration.
 * Same approach as Pure Meat implementation.
 */
export const getPaystackKey = async (token) => {
  try {
    const response = await axios.get(`${BASE_URL}/getPaystackKey`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('getPaystackKey response:', data);
    
    if (response.status === 200 && data.status === 200 && data.data) {
      return { 
        success: true, 
        key: data.data 
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to fetch Paystack key'
      };
    }
  } catch (error) {
    console.error('Get Paystack key API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.'
    };
  }
};

/**
 * Get Flutterwave Key from Backend
 * Backend controls whether to return test or live key based on admin configuration.
 * Same approach as Pure Meat implementation.
 */
export const getFlutterwaveKey = async (token) => {
  try {
    const response = await axios.get(`${BASE_URL}/getFlutterwaveKey`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('getFlutterwaveKey response:', data);
    
    if (response.status === 200 && data.status === 200 && data.data) {
      return { 
        success: true, 
        key: data.data 
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to fetch Flutterwave key'
      };
    }
  } catch (error) {
    console.error('Get Flutterwave key API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.'
    };
  }
};

// Create Stripe token for card
export const createStripeToken = async (cardData, token) => {
  try {
    const formBody = toFormData(cardData);
    
    const response = await axios.post(`${BASE_URL}/payments/createStripeToken`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('createStripeToken response:', data);
    
    if (response.status === 200 && data.status === 200 && data.success && data.success.id) {
      return { 
        success: true, 
        token: data.success 
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to create Stripe token'
      };
    }
  } catch (error) {
    console.error('Create Stripe token API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Failed to create card token'
    };
  }
};

// Add Stripe card to customer
export const addStripeCard = async (cardToken, customerId, token) => {
  try {
    const formBody = toFormData({
      token: cardToken,
      id: customerId
    });
    
    const response = await axios.post(`${BASE_URL}/payments/addStripeCards`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('addStripeCard response:', data);
    
    if (response.status === 200 && data.status === 200 && data.success && data.success.id) {
      return { 
        success: true, 
        card: data.success 
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to add card'
      };
    }
  } catch (error) {
    console.error('Add Stripe card API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Failed to add card to customer'
    };
  }
};

// Create Stripe customer
export const createStripeCustomer = async (customerData, token) => {
  try {
    const formBody = toFormData(customerData);
    
    const response = await axios.post(`${BASE_URL}/payments/createCustomer`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('createStripeCustomer response:', data);
    
    if (response.status === 200 && data.status === 200 && data.success && data.success.id) {
      return { 
        success: true, 
        customer: data.success 
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to create customer'
      };
    }
  } catch (error) {
    console.error('Create Stripe customer API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Failed to create Stripe customer'
    };
  }
};

// Apply coupon/offer validation
export const applyCoupon = async (couponCode, userId, cartTotal, token) => {
  try {
    // First get available offers
    const offersResult = await getOffers(token);
    
    if (!offersResult.success || !offersResult.data) {
      return {
        success: false,
        message: 'Failed to fetch offers'
      };
    }
    
    // Find matching coupon
    const matchedOffer = offersResult.data.find(
      offer => offer.name && offer.name.toLowerCase() === couponCode.toLowerCase()
    );
    
    if (!matchedOffer) {
      return {
        success: false,
        message: 'Coupon not found'
      };
    }
    
    // Validate minimum cart value
    const minAmount = parseFloat(matchedOffer.min) || 0;
    if (cartTotal < minAmount) {
      return {
        success: false,
        message: `Minimum cart value must be $${minAmount} or more`
      };
    }
    
    return {
      success: true,
      data: matchedOffer,
      message: 'Coupon applied successfully'
    };
    
  } catch (error) {
    console.error('Apply coupon API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Failed to apply coupon'
    };
  }
};

// ============================================
// ORDERS APIs
// ============================================

// Create order
export const createOrder = async (orderData, token) => {
  try {
    console.log('Creating order with data:', orderData);
    
    const formBody = toFormData(orderData);
    
    const response = await axios.post(`${BASE_URL}/orders/create`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('createOrder response:', data);
    
    if (response.status === 200 && data.status === 200 && data.data && data.data.id) {
      return { 
        success: true, 
        data: data.data,
        orderId: data.data.id
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to create order'
      };
    }
  } catch (error) {
    console.error('Create order API error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Network error. Please try again.'
    };
  }
};

// Send notification to stores
export const sendStoreNotification = async (notificationData, token) => {
  try {
    const formBody = toFormData(notificationData);
    
    const response = await axios.post(`${BASE_URL}/notification/sendToStore`, formBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    console.log('sendStoreNotification response:', data);
    
    return { success: true };
  } catch (error) {
    console.error('Send store notification API error:', error);
    return { success: false };
  }
};

// Send order notification email to the store
export const sendStoreOrderNotificationEmail = async (orderData, token) => {
  try {
    const param = {
      id: orderData.id,
      mediaURL: orderData.mediaURL || MEDIA_URL,
      subject: orderData.subject || 'New Order Received',
      email: orderData.store_email || 'store@tazatropics.com', // Explicitly for store
      username: 'Store Admin', // Or relevant store name
      store_phone: orderData.store_phone || '1234567890',
      store_email: orderData.store_email || 'store@tazatropics.com',
      header_text: 'New Order Notification',
      thank_you_text: 'A new order has been placed.',
    };

    console.log('Sending order notification email to store with params:', param);

    const formBody = toFormData(param);

    const response = await axios.post(
      `${BASE_URL}/orders/sendMailForOrders`,
      formBody,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      }
    );

    console.log('Order notification email response (store):', response.data);

    if (response.data.success || (response.data.status === 200 && response.data.data)) {
      return { success: true, data: response.data };
    }
    return {
      success: false,
      message: response.data.message || 'Failed to send order notification email to store',
      details: response.data
    };
  } catch (error) {
    console.error('Send order notification email to store error:', error);
    console.error('Error status:', error.response?.status);
    console.error('Error response data:', error.response?.data);

    return {
      success: false,
      message: error.response?.data?.message || error.response?.data?.error || 'Failed to send order notification email to store',
      details: error.response?.data
    };
  }
};
